import useCustomModal from 'components/CustomConfirm';
import CustomConfirmDialog from 'components/CustomConfirm/CustomConfirmDialog';
import { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useRecoilState, useResetRecoilState } from 'recoil';
import { useUserState } from 'services/authentication.service';
import { createNewBooking } from 'services/booking.service';
import { getDefaultPaymentMethod, setDefaultPaymentMethodLocal } from 'services/payments.service';
import { useTrackEvent } from 'util/trackEvent';
import { START_STEP } from './constants';
import { bookingState, cartVisibleState, clearCartState, invalidCartState, isCartOpenState, packagesState, servicesState, sessionTokenState, stepState, timeSlotState } from './state';

const useCreateBooking = () => {
  const [booking, setBooking] = useRecoilState(bookingState);
  const resetBooking = useResetRecoilState(bookingState)
  const [packages, setPackages] = useRecoilState(packagesState);
  const [services, setServices] = useRecoilState(servicesState);
  const [timeslot, setTimeslot] = useRecoilState(timeSlotState);
  const [sessionToken, setSessionToken] = useRecoilState(sessionTokenState);
  const [invalidCart, setInvalidCart] = useRecoilState(invalidCartState);
  const [step, setStep] = useRecoilState(stepState);
  const [cartVisible, setCartVisible] = useRecoilState(cartVisibleState);
  const [cartOpen, setCartOpen] = useRecoilState(isCartOpenState);
  const [clearCart, setClearCart] = useRecoilState(clearCartState);

  const [loading, setLoading] = useState(false);
  const confirm = useCustomModal()
  const trackEvent = useTrackEvent()
  const { getUser } = useUserState()
  const history = useHistory()

  const clearState = useCallback(
    () => {
      setDefaultPaymentMethodLocal(null)
      resetBooking()
      setTimeslot(null)
      setLoading(false)
      setInvalidCart(false)
      setCartOpen(false)
      setCartVisible(false)
      setSessionToken(null)
      setClearCart(true)
      setStep(START_STEP)
      history.replace(START_STEP, { internal: true })
    },
    [],
  );

  const doCreateBooking = useCallback(
    async () => {
      try {
        const payment = await getDefaultPaymentMethod()
        setLoading(true)
        const user = await getUser()
        const clientId = user?.client?.id || user?.user?.id
        const params = {
          lat: booking?.location?.latitude,
          lng: booking?.location?.longitude,
          location_id: booking?.location?.id,
          address: booking?.location?.address,
          notes: booking?.notes,
          payment_method_token: payment?.token,
          should_check_for_payment: true,
          should_check_for_medical_id: true,
          client_type: "web",
          should_check_for_screening: true,
          // BOOKING_FAVORITE_FIRST = 0
          // BOOKING_FAVORITE_ONLY = 1
          // BOOKING_ANYONE = 2
          booking_option: 2, //ANYONE
          type: booking?.serviceCategory?.type?.id || booking?.serviceCategory?.featured_service?.type_id,
          extended_package_items: services?.map(x => x?.id),
          extended_packages: packages?.map(x => x?.id),
          gender: booking?.profile?.gender ?? 'M',
          require_location_confirmation: false,
          service_addon_order: [
            {
              type: "service",
              "service_addon_id": booking?.serviceCategory?.type?.id || booking?.serviceCategory?.featured_service?.type_id,
              priority: 1,
            }
          ],
          duration: 60,
          date_scheduled: timeslot,
          should_check_high_availability: true,
          code: booking?.promo?.code,
          code_type: booking?.promo?.type,
        }
        const res = await createNewBooking(clientId, params)
        clearState()
        history.push(`/bookings/${res?.id}`)
        window?.location?.reload()
      } catch (error) {
        setLoading(false)
      }
    },
    [booking, packages, services, timeslot],
  );
  const createBooking = useCallback(
    async () => {
      confirm(props => (
        <CustomConfirmDialog {...props}
          description='Are you sure you want to create this booking?'
          title='Create Booking'
          onConfirm={async () => {
            trackEvent('Create booking - OK', { dialog: 'createBooking', })
            await doCreateBooking()
          }}
          onCancel={() => {
            trackEvent('Create booking - Cancel', { dialog: 'createBooking', })
            props.onClose()
          }}
        />
      ))
    },
    [booking, packages, services],
  );

  return { createBooking, loading, clearState }
}

export { useCreateBooking };
